import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ClipboardList, CheckCircle, XCircle, Clock, ChevronRight, Calendar } from 'lucide-react'
import { useStore } from '@/contexts/StoreContext'
import { useAuth } from '@/contexts/AuthContext'
import { useTheme } from '@/contexts/ThemeContext'
import { formService, MySubmission } from '@/lib/services/form.service'
import PageHero from '@/components/common/PageHero'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { formatDistanceToNow } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { Caixa } from '@/construtor/caixa'

const statusConfig = {
  PENDING:  { label: 'Pendente',  icon: Clock,         className: 'bg-warning/10 text-warning border-warning/30' },
  APPROVED: { label: 'Aprovado',  icon: CheckCircle,   className: 'bg-success/10 text-success border-success/30' },
  REJECTED: { label: 'Recusado',  icon: XCircle,       className: 'bg-destructive/10 text-destructive border-destructive/30' },
}

export default function MySubmissionsPage() {
  const navigate = useNavigate()
  const { store } = useStore()
  const { isAuthenticated, openLoginModal } = useAuth()
  const { config } = useTheme()
  const [submissions, setSubmissions] = useState<MySubmission[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false)
      return
    }
    if (store?.id) loadSubmissions()
  }, [store, isAuthenticated])

  async function loadSubmissions() {
    if (!store?.id) return
    try {
      setLoading(true)
      const data = await formService.getMySubmissions(store.id)
      setSubmissions(data.submissions)
    } catch {
      setSubmissions([])
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div>
        <PageHero title="Minhas Respostas" breadcrumbs={[{ label: 'Minhas Respostas' }]} />
        <div className="container mx-auto px-4 md:px-6 lg:px-8 py-16 flex justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
        </div>
      </div>
    )
  }

  return (
    <div>
      <PageHero
        title="Minhas Respostas"
        breadcrumbs={[{ label: 'Minhas Respostas' }]}

      />

      <div className="container mx-auto px-4 md:px-6 lg:px-8 py-8">
        {!isAuthenticated ? (
          <Caixa id="formularios.minhas.aviso" miolo>
          <Card className="border-border">
            <CardContent className="p-12 flex flex-col items-center text-center gap-4">
              <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center">
                <ClipboardList className="w-10 h-10 text-primary" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-foreground mb-2">Login necessário</h3>
                <p className="text-muted-foreground">Faça login para ver seus formulários respondidos.</p>
              </div>
              <Button onClick={openLoginModal}>Entrar</Button>
            </CardContent>
          </Card>
          </Caixa>
        ) : submissions.length === 0 ? (
          <Caixa id="formularios.minhas.vazio" miolo>
          <Card className="border-border">
            <CardContent className="p-12 flex flex-col items-center text-center gap-4">
              <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center">
                <ClipboardList className="w-10 h-10 text-primary" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-foreground mb-2">Nenhuma resposta</h3>
                <p className="text-muted-foreground">Você ainda não respondeu nenhum formulário.</p>
              </div>
              <Button variant="outline" onClick={() => navigate('/formularios')}>Ver formulários</Button>
            </CardContent>
          </Card>
          </Caixa>
        ) : (
          <Caixa id="formularios.minhas">
          <div className="space-y-4 max-w-2xl mx-auto">
            {submissions.map((sub) => {
              const cfg = statusConfig[sub.status] ?? statusConfig.PENDING
              const Icon = cfg.icon
              return (
                <Caixa id="formularios.minhas.item" repetida key={sub.id}>
                <Card
                  key={sub.id}
                  className="border-border hover:border-primary/40 transition-all cursor-pointer group"
                  onClick={() => navigate(`/formularios/${sub.formSlug}`)}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                        <ClipboardList className="w-5 h-5 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors leading-tight">
                            {sub.formTitle}
                          </h3>
                          <Badge className={`border flex-shrink-0 text-xs px-2 py-0.5 ${cfg.className}`}>
                            <Icon className="w-3 h-3 mr-1" />
                            {cfg.label}
                          </Badge>
                        </div>

                        <div className="flex items-center gap-1 text-xs text-muted-foreground mb-2">
                          <Calendar className="w-3 h-3" />
                          <span>
                            Respondido {formatDistanceToNow(new Date(sub.submittedAt), { addSuffix: true, locale: ptBR })}
                          </span>
                        </div>

                        {sub.observation && (
                          <div className={`mt-2 p-3 rounded-lg border text-sm ${cfg.className}`}>
                            <p className="font-medium mb-0.5">Observação:</p>
                            <p className="opacity-90">{sub.observation}</p>
                          </div>
                        )}

                        {sub.reviewedAt && (
                          <p className="text-xs text-muted-foreground mt-2">
                            Avaliado {formatDistanceToNow(new Date(sub.reviewedAt), { addSuffix: true, locale: ptBR })}
                          </p>
                        )}
                      </div>
                      <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0 mt-1" />
                    </div>
                  </CardContent>
                </Card>
                </Caixa>
              )
            })}
          </div>
          </Caixa>
        )}
      </div>
    </div>
  )
}
